import { Alert, Button, Card, message, Popconfirm, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';

import { queryErrorMessage } from '../ClubRegistration/types';

const { Title, Text } = Typography;

type CorrectionStatus = 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';

const STATUS_TABS: { key: CorrectionStatus; label: string }[] = [
  { key: 'PENDING_APPROVAL', label: 'Хүлээгдэж буй' },
  { key: 'APPROVED', label: 'Зөвшөөрсөн' },
  { key: 'REJECTED', label: 'Татгалзсан' },
];

// A Correction (CONTEXT.md) on a student's Lunch order, of any channel.
interface CorrectionRow {
  id: number;
  channel: 'CLASS' | 'KIOSK';
  correction_status: CorrectionStatus;
  student_name: string;
  student_code: string | null;
  student_gender: string | null;
  class_name: string;
  lunch_date: string | null;
  correction_reason: string | null;
  amount: number | null;
  reviewed_at: string | null;
  reviewed_user_name: string | null;
}

function formatAmount(n: number | null): string {
  if (n == null) return '—';
  return `${new Intl.NumberFormat('mn-MN').format(n)}₮`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const ClassLunchCorrections = () => {
  const { level, runQuery } = useScreen();
  // Approving and rejecting are edit-level Queries; the server refuses them for view.
  const canEdit = level === 'edit';
  const [rows, setRows] = useState<CorrectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [status, setStatus] = useState<CorrectionStatus>('PENDING_APPROVAL');

  const loadRows = useCallback(() => {
    setLoading(true);
    runQuery<CorrectionRow>('lunch_corrections', { status })
      .then((items) => {
        setError(null);
        setRows(items);
      })
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [status, runQuery]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  async function approve(id: number) {
    setActingId(id);
    try {
      await runQuery('lunch_correction_approve', { orderId: id });
      message.success('Зөвшөөрөгдөж, буцаалт хийгдлээ.');
      loadRows();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setActingId(null);
    }
  }


  async function reject(id: number) {
    setActingId(id);
    try {
      await runQuery('lunch_correction_reject', { orderId: id });
      message.success('Татгалзлаа.');
      loadRows();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setActingId(null);
    }
  }


  const columns: ColumnsType<CorrectionRow> = [
    { title: 'Анги', dataIndex: 'class_name', width: 100 },
    { title: 'Огноо', dataIndex: 'lunch_date', width: 110, render: formatDate },
    {
      title: 'Суваг',
      dataIndex: 'channel',
      width: 110,
      render: (v: CorrectionRow['channel']) => (v === 'KIOSK' ? <Tag>Киоск</Tag> : <Tag>Анги</Tag>),
    },
    { title: 'Сурагч', dataIndex: 'student_name' },
    { title: 'Код', dataIndex: 'student_code', width: 100, render: (v: string | null) => v ?? '—' },
    {
      title: 'Хүйс',
      dataIndex: 'student_gender',
      width: 100,
      render: (v: string | null) => v ?? '—',
    },
    { title: 'Дүн', dataIndex: 'amount', width: 120, align: 'right', render: formatAmount },
    { title: 'Багшийн бичсэн шалтгаан', dataIndex: 'correction_reason' },
  ];

  const reviewedColumns: ColumnsType<CorrectionRow> = [
    {
      title: 'Төлөв',
      dataIndex: 'correction_status',
      width: 120,
      render: (v: CorrectionStatus) =>
        v === 'APPROVED' ? <Tag color="green">Зөвшөөрсөн</Tag> : <Tag color="red">Татгалзсан</Tag>,
    },
    {
      title: 'Хянасан',
      dataIndex: 'reviewed_user_name',
      width: 160,
      render: (v: string | null) => v ?? '—',
    },
    { title: 'Хянасан огноо', dataIndex: 'reviewed_at', width: 150, render: formatDateTime },
  ];

  const actionColumns: ColumnsType<CorrectionRow> = [
    {
      title: '',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Popconfirm
            title="Зөвшөөрөх үү?"
            description={`${row.student_name}-д ${formatAmount(row.amount)} буцаалтын гүйлгээ үүснэ. Энэ үйлдлийг буцаах боломжгүй.`}
            okText="Зөвшөөрөх"
            cancelText="Болих"
            onConfirm={() => approve(row.id)}
          >
            <Button size="small" type="primary" loading={actingId === row.id}>
              Зөвшөөрөх
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Татгалзах уу?"
            description={`${row.student_name}-ийн засварын хүсэлтийг татгалзана. Мөнгөн гүйлгээ хийгдэхгүй.`}
            okText="Татгалзах"
            okButtonProps={{ danger: true }}
            cancelText="Болих"
            onConfirm={() => reject(row.id)}
          >
            <Button size="small" danger loading={actingId === row.id}>
              Татгалзах
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <Title level={4} style={{ marginBottom: 4 }}>
          Хоолны бүртгэлийн засварын хүсэлтүүд
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Багш нарын илгээсэн, буруу бүртгэсэн эсвэл киоскоор авсан ч идээгүй хоолыг буцаах хүсэлтүүд. Зөвшөөрвөл
          сурагчийн картад автоматаар буцаалтын гүйлгээ үүснэ.
        </Text>

        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

        <Tabs
          activeKey={status}
          onChange={(key) => {
            setRows([]);
            setStatus(key as CorrectionStatus);
          }}
          items={STATUS_TABS.map((t) => ({ key: t.key, label: t.label }))}
        />

        {!error && (
          <Table
            rowKey="id"
            columns={[
              ...columns,
              ...(status === 'PENDING_APPROVAL' ? (canEdit ? actionColumns : []) : reviewedColumns),
            ]}
            dataSource={rows}
            loading={loading}
            pagination={status === 'PENDING_APPROVAL' ? false : { pageSize: 20 }}
          />
        )}
      </Card>
    </LayoutWithMenu>
  );
};

export default ClassLunchCorrections;
